import { Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS } from "@/lib/businessInfo";

const Footer = () => {
    const { lang, t } = useLanguage();
    const gu = lang === "gu" ? "font-gujarati" : "";
    const year = new Date().getFullYear();

    const quickLinks = [
        { id: "about", key: "nav.about" },
        { id: "services", key: "nav.services" },
        { id: "coverage", key: "nav.coverage" },
        { id: "contact", key: "nav.contact" },
    ];

    const serviceKeys = ["vehicle", "car", "commercial", "home", "mortgage", "insurance"];

    const handleNav = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <footer
            data-testid="site-footer"
            className="bg-slate-950 text-slate-300 pt-20 pb-8 relative overflow-hidden"
        >
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#0B308A]/30 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="flex items-center">
                            <img
                                src="/Shree-Ganesh-Logo-White.png"
                                alt="Shree Ganesh Financial Service"
                                className="h-16 w-auto object-contain"
                            />
                        </div>
                        <p
                            className={`mt-6 text-slate-400 text-[14.5px] leading-relaxed max-w-md ${gu}`}
                        >
                            {t("footer.tagline")}
                        </p>

                        <div className="mt-7 space-y-2.5 text-[14px]">
                            <a
                                href={`tel:${BUSINESS.phoneTel[0]}`}
                                className="flex items-center gap-3 text-slate-300 hover:text-white"
                            >
                                <Phone className="w-4 h-4 text-[#FACC15]" />
                                {BUSINESS.phones[0]} • {BUSINESS.phones[1]}
                            </a>
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                className="flex items-center gap-3 text-slate-300 hover:text-white break-all"
                            >
                                <Mail className="w-4 h-4 text-[#FACC15]" />
                                {BUSINESS.email}
                            </a>
                            <div className="flex items-start gap-3 text-slate-400">
                                <MapPin className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" />
                                <span>{BUSINESS.address}</span>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <div
                            className={`text-[11px] uppercase tracking-[0.22em] font-semibold text-white ${gu}`}
                        >
                            {t("footer.quick")}
                        </div>
                        <ul className="mt-5 space-y-2.5">
                            {quickLinks.map((l) => (
                                <li key={l.id}>
                                    <button
                                        onClick={() => handleNav(l.id)}
                                        className={`text-[14px] text-slate-400 hover:text-white transition-colors ${gu}`}
                                    >
                                        {t(l.key)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-4">
                        <div
                            className={`text-[11px] uppercase tracking-[0.22em] font-semibold text-white ${gu}`}
                        >
                            {t("footer.services")}
                        </div>
                        <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5">
                            {serviceKeys.map((k) => (
                                <li
                                    key={k}
                                    className={`text-[14px] text-slate-400 ${gu}`}
                                >
                                    {t(`services.items.${k}.title`)}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12.5px] text-slate-500">
                    <div className={gu}>
                        © {year} {BUSINESS.name}. {t("footer.rights")}
                    </div>
                    <div>
                        Crafted with care in Ahmedabad, Gujarat 🇮🇳
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
